import { useNavigate } from 'react-router-dom';
import { Grid, Toast, ActionSheet, Dialog } from 'antd-mobile';
import { UserCircleOutline, GlobalOutline, SoundOutline } from 'antd-mobile-icons';
import {
  PickupArrivalIcon,

  BaggingIcon,
  UnbaggingIcon,
  DepartureIcon,
  SortingIcon,
  WhsArrivalIcon,
  ReturnIcon,
  ReturnToWhsIcon,
  ReturnArrivalIcon,
  HandoverIcon,
  TrackPackageIcon,
  WeightParcelIcon,
  DestroyIcon,
  ExceptionIcon,
  ShipOutIcon,
  ShipInIcon,
  PutAwayIcon,
  PickingIcon,
  CartonScanIcon,
  BagMergeIcon,
  BagReshipIcon
} from './icons';
import { gql, useQuery } from '@apollo/client';
import { getUserInfo, hasHubOperationPermission, hasWarehouseOperationPermission, isPickupStaff, isSortingStaff, isReturnsStaff, logout, isWarehouseManager, isHubManager } from '../../utils/auth';
import { t } from '../../i18n';
import { useGetCommonConfigByKey, useCheckWarehouseIdInGrayConfig } from '../../graphql/queries/commonConfigQueries';
import {
  getAppVersion,
  isBluetoothScaleAvailable,
  isCameraAvailablePDA
} from '../../utils/appRuntime';
import { useEffect, useState } from 'react';
import { GET_ACCOUNT_BIND_WAREHOUSE, GetAccountBindWarehouseResponse } from '../../graphql/queries/warehouseQueries';
import { WarehouseBusinessType } from '../../enums/WarehouseTypeEnum';
import { useScanSoundSettingsStore } from '../../store/scanSoundSettingsStore';

const Header = ({ appVersion, warehouse }: { appVersion: string; warehouse?: GetAccountBindWarehouseResponse['warehouse'] }) => {
  const userInfo = getUserInfo();
  const navigate = useNavigate();
  const masterEnabled = useScanSoundSettingsStore((s) => s.masterEnabled);

  const handleUserClick = () => {
    const actions = [
      {
        text: `${t('pda.common.user')}: ${userInfo?.account.name || '--'}`,
        key: 'user-info',
        disabled: true,
      },
      {
        text: `${t('pda.common.version')} ${appVersion}`,
        key: 'version',
        disabled: true,
      },
      {
        text: t('pages.useMenu.language'),
        key: 'language',
        icon: <GlobalOutline />,
      },
      ...(masterEnabled ? [{
        text: t('pda.scanSetting.entry'),
        key: 'scanSetting',
        icon: <SoundOutline />,
      }] : []),
      {
        text: t('pages.useMenu.signOut'),
        key: 'logout',
        danger: true,
      },
    ];

    const handler = ActionSheet.show({
      actions,
      onAction: (action) => {
        if (action.key === 'logout') {
          Dialog.confirm({
            content: t('pda.common.confirmLogout'),
            confirmText: t('pda.common.confirm'),
            cancelText: t('pda.common.cancel'),
            onConfirm: async () => {
              await logout('/pda/login');
            },
          });
        } else if (action.key === 'language') {
          handler.close();
          navigate('/pda/language');
        } else if (action.key === 'scanSetting') {
          handler.close();
          navigate('/pda/scan-setting');
        }
      },
    });
  };
  return (
    <div className="p-4 flex justify-between items-center border-b">
      <div className="font-bold">{warehouse?.name || '--'}</div>
      <div className="flex items-center gap-3">
        <UserCircleOutline className="w-6 h-6 cursor-pointer" onClick={handleUserClick} />
      </div>
    </div>
  )
};

export default function PdaHomePage() {
  const navigate = useNavigate();
  const { data: warehouseData } = useQuery<GetAccountBindWarehouseResponse>(GET_ACCOUNT_BIND_WAREHOUSE);

  const [appVersion, setAppVersion] = useState<string>('V1.0'); // 默认版本
  const [bluetoothScaleAvailable, setBluetoothScaleAvailable] = useState<boolean>(false); // 蓝牙电子秤是否可用
  const [cameraAvailablePDA, setCameraAvailablePDA] = useState<boolean>(false); // 拍照功能是否可用
  const pickupArrivalOnly = isPickupStaff();
  const canOperateHub = hasHubOperationPermission();
  const isSortingStaffRole = isSortingStaff();
  const isReturnsStaffRole = isReturnsStaff();
  const canOperateWarehouse = hasWarehouseOperationPermission();
  const isManager = isWarehouseManager() || isHubManager();

  // 查询大包扫描入口的灰度配置（逗号分隔的仓库 ID 白名单）
  const { data: cartonScanConfig } = useGetCommonConfigByKey('pda.client.carton.scan.operate.hub');

  const shouldShowCartonScan = () => {
    if (!warehouseData?.warehouse?.id || !cartonScanConfig?.getCommonConfigByKey) {
      return false;
    }
    // 预分拣大包接收仅尾程 hub 作业：绑定仓非尾程时不展示入口（与白名单叠加）
    if (warehouseData.warehouse.businessType !== WarehouseBusinessType.LAST_MILE) return false;
    const configValue = cartonScanConfig.getCommonConfigByKey.configValue;
    if (configValue.toLowerCase().includes('all')) return true;
    const allowedWarehouseIds = configValue.split(',').map(id => id.trim());
    return allowedWarehouseIds.includes(String(warehouseData.warehouse.id));
  };

  // 查询合包入口的灰度配置（逗号分隔的仓库 ID 白名单或 "All"）
  const { data: bagMergeConfig } = useGetCommonConfigByKey('pda.merge.bag.operate.hub');

  // 查询合包入口的灰度配置（逗号分隔的仓库 ID 白名单或 "All"）
  const { data: pdaScanReturnShowConfig } = useGetCommonConfigByKey('pda.scan.bag.container.operate.hub');

  // 改流向入口灰度：复用后端 reship 白名单 key（warehouse.bag_reship.warehouse_whitelist），由服务端校验当前仓是否命中，单一真相源
  const warehouseIdAsNumber = warehouseData?.warehouse?.id ?? null;
  const { data: bagReshipGrayConfig } = useCheckWarehouseIdInGrayConfig('warehouse.bag_reship.warehouse_whitelist', warehouseIdAsNumber);
  const shouldShowBagReship = bagReshipGrayConfig?.checkWarehouseIdInGrayConfig ?? false;

  const shouldShowBagMerge = () => {
    if (!warehouseData?.warehouse?.id || !bagMergeConfig?.getCommonConfigByKey) {
      return false;
    }
    const configValue = bagMergeConfig.getCommonConfigByKey.configValue;
    if (configValue.toLowerCase().includes('all')) return true;
    const allowedWarehouseIds = configValue.split(',').map(id => id.trim());
    return allowedWarehouseIds.includes(String(warehouseData.warehouse.id));
  };

  const shouldPdaScanReturnShow = () => {
    if (!warehouseData?.warehouse?.id || !pdaScanReturnShowConfig?.getCommonConfigByKey || !pdaScanReturnShowConfig?.getCommonConfigByKey.isActive) {
      return false;
    }
    const configValue = pdaScanReturnShowConfig.getCommonConfigByKey.configValue;
    if (configValue.toLowerCase().includes('all')) return true;
    const allowedWarehouseIds = configValue.split(',').map(id => id.trim());
    return allowedWarehouseIds.includes(String(warehouseData.warehouse.id));
  };


  useEffect(() => {
    // 获取应用版本号
    getAppVersion().then(version => {
      setAppVersion(version);
    });
    // 检查蓝牙电子秤功能是否可用
    setBluetoothScaleAvailable(isBluetoothScaleAvailable());
    // 检查拍照功能是否可用
    setCameraAvailablePDA(isCameraAvailablePDA());
    // 每次回到 PDA 首页都从后端拉取最新扫描配置（后端单一真相 + 本地缓存）；后台改的配置下次回首页即生效，无需重新登录
    void useScanSoundSettingsStore.getState().loadFromBackend();

    // 进入 PDA 首页时清空“装卸/列表页”筛选条件
    // 这样回到顶部菜单后列表不会携带上次筛选状态
    try {
      window.localStorage.removeItem('pda_ship_in_filters_v1');
      window.localStorage.removeItem('pda_ship_out_filters_v1');
    } catch {
      // ignore: storage unavailable
    }
  }, []);

  const navigateTo = (path: string) => {
    navigate(path);
  };

  return (
    <div className=" bg-white">
      {/* 顶部区域 */}
      <Header appVersion={appVersion} warehouse={warehouseData?.warehouse} />

      <div className="p-4 bg-gray-50 flex flex-col gap-5">
        {/* HUB 区域 */}
        {
          (pickupArrivalOnly || canOperateHub || isSortingStaffRole || isReturnsStaffRole) && <div>
          <div className="font-bold text-base mb-2">{t('pda.pages.hub')}</div>
          <Grid columns={3} gap={8}>
            {!isSortingStaffRole && (
              <Grid.Item onClick={() => navigateTo('/pda/arrival')}>
                <div className="flex flex-col items-center bg-white rounded-xl p-2">
                  <div className="w-14 h-14 rounded-lg bg-blue-500 flex items-center justify-center mb-1">
                    <PickupArrivalIcon />
                  </div>
                  <span className="text-xs">{t('pda.pages.arrival.arrival')}</span>
                </div>
              </Grid.Item>
            )}

            {!pickupArrivalOnly && (
              <>

                {!isSortingStaffRole && (
                  <Grid.Item onClick={() => navigateTo('/pda/return-arrival')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2">
                      <div className="w-14 h-14 rounded-lg bg-blue-500 flex items-center justify-center mb-1">
                        <ReturnArrivalIcon />
                      </div>
                      <span className="text-xs">{t('pda.pages.returnArrival.returnArrival')}</span>
                    </div>
                  </Grid.Item>
                )}
                {!isSortingStaffRole && (
                  <Grid.Item onClick={() => navigateTo('/pda/departure')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2">
                      <div className="w-14 h-14 rounded-lg bg-blue-500 flex items-center justify-center mb-1">
                        <DepartureIcon />
                      </div>
                      <span className="text-xs">{t('pda.pages.departure.departure')}</span>
                    </div>
                  </Grid.Item>
                )}
                {!isReturnsStaffRole && (
                  <Grid.Item onClick={() => navigateTo(shouldPdaScanReturnShow() ? '/pda/bagging' : '/pda/bagging-old')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2 h-full">
                      <div className="w-14 h-14 rounded-lg bg-blue-500 flex items-center justify-center mb-1">
                        <BaggingIcon />
                      </div>
                      <span className="text-xs">{t('pda.pages.bagging.baggingScan')}</span>
                    </div>
                  </Grid.Item>
                )}
                {!isSortingStaffRole && !isReturnsStaffRole && (
                  <Grid.Item onClick={() => navigateTo('/pda/sorting')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2">
                      <div className="w-14 h-14 rounded-lg bg-blue-500 flex items-center justify-center mb-1">
                        <SortingIcon />
                      </div>
                      <span className="text-xs">{t('pda.pages.sorting.sorting')}</span>
                    </div>
                  </Grid.Item>
                )}
                <Grid.Item onClick={() => navigateTo('/pda/search')}>
                  <div className="flex flex-col items-center bg-white rounded-xl p-2">
                    <div className="w-14 h-14 rounded-lg bg-blue-500 flex items-center justify-center mb-1">
                      <TrackPackageIcon />
                    </div>
                    <span className="text-xs">{t('pda.pages.trackPackage.trackPackage')}</span>
                  </div>
                </Grid.Item>
                {bluetoothScaleAvailable && !isSortingStaffRole && !isReturnsStaffRole && (
                  <Grid.Item onClick={() => navigateTo('/pda/weightParcel')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2">
                      <div className="w-14 h-14 rounded-lg bg-blue-500 flex items-center justify-center mb-1">
                        <WeightParcelIcon />
                      </div>
                      <span className="text-xs">{t('pda.pages.weightParcel.weightParcel')}</span>
                    </div>
                  </Grid.Item>
                )}
                {!isSortingStaffRole && !isReturnsStaffRole && warehouseData?.warehouse?.businessType === 1 && (
                  <Grid.Item onClick={() => navigateTo('/pda/handover')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2">
                      <div className="w-14 h-14 rounded-lg bg-orange-500 flex items-center justify-center mb-1">
                        <HandoverIcon />
                      </div>
                      <span className="text-xs">{t('pda.pages.handover.handover')}</span>
                    </div>
                  </Grid.Item>
                )}
                {!isSortingStaffRole && (
                  <Grid.Item onClick={() => navigateTo('/pda/return')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2">
                      <div className="w-14 h-14 rounded-lg bg-blue-500 flex items-center justify-center mb-1">
                        <ReturnIcon />
                      </div>
                      <span className="text-xs">{t('pda.pages.return.return')}</span>
                    </div>
                  </Grid.Item>
                )}
                {(isManager || isReturnsStaffRole) && (
                  <Grid.Item onClick={() => navigateTo('/pda/destroy')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2">
                      <div className="w-14 h-14 rounded-lg bg-blue-500 flex items-center justify-center mb-1">
                        <DestroyIcon />
                      </div>
                      <span className="text-xs">{t('pda.pages.destroy.destroy')}</span>
                    </div>
                  </Grid.Item>
                )}
                {!isSortingStaffRole && !isReturnsStaffRole && (
                  <Grid.Item onClick={() => navigateTo('/pda/unbagging')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2 h-full">
                      <div className="w-14 h-14 rounded-lg bg-blue-500 flex items-center justify-center mb-1">
                        <UnbaggingIcon />
                      </div>
                      <span className="text-xs">{t('pda.pages.unbagging.unbaggingScan')}</span>
                    </div>
                  </Grid.Item>
                )}
                {!isSortingStaffRole && !isReturnsStaffRole && shouldShowCartonScan() && (
                  <Grid.Item onClick={() => navigateTo('/pda/carton-scan')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2 h-full">
                        <div className="w-14 h-14 rounded-lg bg-blue-500 flex items-center justify-center mb-1">
                            <CartonScanIcon />
                        </div>
                        <span className="text-xs">{t('pda.pages.cartonScan.cartonScan')}</span>
                    </div>
                  </Grid.Item>
                )}
                {/* 合包扫描入口；可见角色：分拣员 / Hub 操作员 / Hub 经理 */}
                {!isReturnsStaffRole && shouldShowBagMerge() && (
                  <Grid.Item onClick={() => navigateTo('/pda/bag-merge-scan')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2 h-full">
                        <div className="w-14 h-14 rounded-lg bg-blue-500 flex items-center justify-center mb-1">
                            <BagMergeIcon />
                        </div>
                        <span className="text-xs">{t('pda.pages.bagMerge.bagMerge')}</span>
                    </div>
                  </Grid.Item>
                )}
                {/* 改流向入口：仅 TT 分拣仓 hub manager/operator + 灰度命中仓可见（后端再次门禁）。
                    canOperateHub 白名单与后端 @RequirePermissions(HUB_MANAGER/HUB_OPERATOR) 对齐——
                    本区块还会放进分拣员/退货员，用排除法容易漏掉后续新增角色 */}
                {canOperateHub && shouldShowBagReship && (
                  <Grid.Item onClick={() => navigateTo('/pda/bag-reship-scan')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2 h-full">
                        <div className="w-14 h-14 rounded-lg bg-blue-500 flex items-center justify-center mb-1">
                            <BagReshipIcon />
                        </div>
                        <span className="text-xs">{t('pda.pages.bagReship.bagReship')}</span>
                    </div>
                  </Grid.Item>
                )}
              </>
            )}
          </Grid>
        </div>
        }

        {/* Vehicle 区域 */}
        {
          !pickupArrivalOnly && canOperateHub && <div>
          <div className="font-bold text-base mb-2">{t('pda.pages.vehicle')}</div>
          <Grid columns={3} gap={8}>
            <Grid.Item onClick={() => navigateTo('/pda/ship-out')}>
              <div className="flex flex-col items-center bg-white rounded-xl p-2">
                <div className="w-14 h-14 rounded-lg bg-purple-500 flex items-center justify-center mb-1">
                  <ShipOutIcon />
                </div>
                <span className="text-xs">{t('pda.pages.shipOut.shipOut')}</span>
              </div>
            </Grid.Item>
            <Grid.Item onClick={() => navigateTo('/pda/ship-in')}>
              <div className="flex flex-col items-center bg-white rounded-xl p-2">
                <div className="w-14 h-14 rounded-lg bg-purple-500 flex items-center justify-center mb-1">
                  <ShipInIcon />
                </div>
                <span className="text-xs">{t('pda.pages.shipIn.shipIn')}</span>
              </div>
            </Grid.Item>
          </Grid>
        </div>
        }

        {/* WHS 区域 */}
        {
          !pickupArrivalOnly && canOperateWarehouse && <div>
          <div className="font-bold text-base mb-2">{t('pda.pages.whs')}</div>
          <Grid columns={3} gap={8}>
            <Grid.Item onClick={() => navigateTo('/pda/whs-arrival')}>
              <div className="flex flex-col items-center bg-white rounded-xl p-2">
                <div className="w-14 h-14 rounded-lg bg-green-500 flex items-center justify-center mb-1">
                  <WhsArrivalIcon />
                </div>
                <span className="text-xs">{t('pda.pages.whsArrival.whsArrival')}</span>
              </div>
            </Grid.Item>
            <Grid.Item onClick={() => navigateTo('/pda/return-to-whs-new')}>
              <div className="flex flex-col items-center bg-white rounded-xl p-2">
                <div className="w-14 h-14 rounded-lg bg-green-500 flex items-center justify-center mb-1">
                  <ReturnToWhsIcon />
                </div>
                <span className="text-xs">{t('pda.pages.returnToWhs.returnToWhs')}</span>
              </div>
            </Grid.Item>
            <Grid.Item onClick={() => navigateTo('/pda/return')}>
              <div className="flex flex-col items-center bg-white rounded-xl p-2">
                <div className="w-14 h-14 rounded-lg bg-green-500 flex items-center justify-center mb-1">
                  <ReturnIcon />
                </div>
                <span className="text-xs">{t('pda.pages.return.return')}</span>
              </div>
            </Grid.Item>
            <Grid.Item onClick={() => navigateTo('/pda/search')}>
              <div className="flex flex-col items-center bg-white rounded-xl p-2">
                <div className="w-14 h-14 rounded-lg bg-green-500 flex items-center justify-center mb-1">
                  <TrackPackageIcon />
                </div>
                <span className="text-xs">{t('pda.pages.trackPackage.trackPackage')}</span>
              </div>
            </Grid.Item>
          </Grid>
        </div>
        }

        {/* WHS Vehicle 区域 单独写，暂定开放装车 */}
        {
            hasWarehouseOperationPermission() && <div>
              <div className="font-bold text-base mb-2">{t('pda.pages.vehicle')}</div>
              <Grid columns={3} gap={8}>
                <Grid.Item onClick={() => navigateTo('/pda/ship-out')}>
                  <div className="flex flex-col items-center bg-white rounded-xl p-2">
                    <div className="w-14 h-14 rounded-lg bg-purple-500 flex items-center justify-center mb-1">
                      <ShipOutIcon />
                    </div>
                    <span className="text-xs">{t('pda.pages.shipOut.shipOut')}</span>
                  </div>
                </Grid.Item>
                <Grid.Item onClick={() => navigateTo('/pda/ship-in')}>
                  <div className="flex flex-col items-center bg-white rounded-xl p-2">
                    <div className="w-14 h-14 rounded-lg bg-purple-500 flex items-center justify-center mb-1">
                      <ShipInIcon />
                    </div>
                    <span className="text-xs">{t('pda.pages.shipIn.shipIn')}</span>
                  </div>
                </Grid.Item>
              </Grid>
            </div>
        }

        {/* 异常区域 - 只有拍照功能可用时才显示 */}
         {((cameraAvailablePDA && !pickupArrivalOnly && (hasHubOperationPermission() || isReturnsStaffRole)) || hasWarehouseOperationPermission()) && <div>
            <div className="font-bold text-base mb-2">{t('pda.pages.exception.exception')}</div>
            {cameraAvailablePDA && !pickupArrivalOnly && (hasHubOperationPermission() || isReturnsStaffRole) && (
              <Grid columns={3} gap={8}>
                <Grid.Item onClick={() => navigateTo('/pda/hubException')}>
                  <div className="flex flex-col items-center bg-white rounded-xl p-2">
                    <div className="w-14 h-14 rounded-lg bg-red-500 flex items-center justify-center mb-1">
                      <ExceptionIcon />
                    </div>
                    <span className="text-xs">{t('pda.pages.exception.exception')}</span>
                  </div>
                </Grid.Item>
              </Grid>
            )}
            {hasWarehouseOperationPermission() && (
              <Grid columns={3} gap={8}>
                {cameraAvailablePDA && !pickupArrivalOnly && (
                  <Grid.Item onClick={() => navigateTo('/pda/warehouseException')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2">
                      <div className="w-14 h-14 rounded-lg bg-red-500 flex items-center justify-center mb-1">
                        <ExceptionIcon />
                      </div>
                      <span className="text-xs">{t('pda.pages.exception.exception')}</span>
                    </div>
                  </Grid.Item>)}
                  <Grid.Item onClick={() => navigateTo('/pda/putaway')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2">
                      <div className="w-14 h-14 rounded-lg bg-red-500 flex items-center justify-center mb-1">
                        <PutAwayIcon />
                      </div>
                      <span className="text-xs">{t('pda.pages.putaway.putaway')}</span>
                    </div>
                  </Grid.Item>
                  <Grid.Item onClick={() => navigateTo('/pda/picking')}>
                    <div className="flex flex-col items-center bg-white rounded-xl p-2">
                      <div className="w-14 h-14 rounded-lg bg-red-500 flex items-center justify-center mb-1">
                        <PickingIcon />
                      </div>
                      <span className="text-xs">{t('pda.pages.picking.picking')}</span>
                    </div>
                  </Grid.Item>
              </Grid>
            )}
          </div>
        }
      </div>
    </div>
  );
} 